import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService } from '../services/modules/auth';
import type { User, RegisterData, LoginCredentials, ProfileUpdateData } from '../services/types/auth';

export interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    login: (credentials: LoginCredentials) => Promise<void>;
    register: (data: RegisterData) => Promise<void>;
    logout: () => void;
    verifyEmail: (token: string) => Promise<void>;
    requestPasswordReset: (email: string) => Promise<void>;
    resetPassword: (token: string, new_password: string) => Promise<void>;
    updateProfile: (data: ProfileUpdateData) => Promise<void>;
    setError: (error: string | null) => void;
    refreshUser: () => Promise<void>;
}

const defaultContextValue: AuthContextType = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
    login: async () => {},
    register: async () => {},
    logout: () => {},
    verifyEmail: async () => {},
    requestPasswordReset: async () => {},
    resetPassword: async () => {},
    updateProfile: async () => {},
    setError: () => {},
    refreshUser: async () => {}
};

export const AuthContext = createContext<AuthContextType>(defaultContextValue);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            AuthService.getCurrentUser(token)
                .then((response) => {
                    setUser({ ...response.user, token });
                    setIsAuthenticated(true);
                })
                .catch(() => {
                    localStorage.removeItem('token');
                })
                .finally(() => {
                    setIsLoading(false);
                });
        } else {
            setIsLoading(false);
        }
    }, []);

    const login = async (credentials: LoginCredentials): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await AuthService.login(credentials);
            const { token, user: userData } = response;
            localStorage.setItem('token', token);
            setUser({ ...userData, token });
            setIsAuthenticated(true);
        } catch (err: any) {
            setError(err.response?.data?.error ?? 'Failed to login');
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (data: RegisterData): Promise<void> => {
        setIsLoading(true);
        try {
            const response = await AuthService.register(data);
            const { token, user: userData } = response;
            localStorage.setItem('token', token);
            setUser({ ...userData, token });
            setIsAuthenticated(true);
        } finally {
            setIsLoading(false);
        }
    };

    const logout = (): void => {
        AuthService.logout();
        setUser(null);
        setIsAuthenticated(false);
        window.location.href = '/';  // Redirect to landing page
    };

    const verifyEmail = async (token: string): Promise<void> => {
        await AuthService.verifyEmail(token);
    };

    const requestPasswordReset = async (email: string): Promise<void> => {
        await AuthService.requestPasswordReset({ email });
    };

    const resetPassword = async (token: string, new_password: string): Promise<void> => {
        await AuthService.resetPassword(token, new_password);
    };

    const updateProfile = async (data: ProfileUpdateData): Promise<void> => {
        if (!user?.token) return;
        setIsLoading(true);
        try {
            const updatedUser = await AuthService.updateProfile(data, user.token);
            setUser({ ...updatedUser, token: user.token });
        } finally {
            setIsLoading(false);
        }
    };

    const refreshUser = async (): Promise<void> => {
        if (!user?.token) return;
        
        try {
            const response = await AuthService.getCurrentUser(user.token);
            setUser({ ...response.user, token: user.token });
        } catch (err) {
            console.error('Failed to refresh user data:', err);
        }
    };

    const contextValue = {
        user,
        isAuthenticated,
        isLoading,
        error,
        login,
        register,
        logout,
        verifyEmail,
        requestPasswordReset,
        resetPassword,
        updateProfile,
        setError,
        refreshUser
    };

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
